<template>
  <div class="home-view">
    <patient-list-header :hospital-name="hospitalName" :user-name="userName" @settings="handleSettings" @logout="handleLogout" />

    <patient-filter-bar
      :total-count="totalPatientCount"
      :filter-counts="filterCounts"
      :selected-filters="selectedFilters"
      :selected-unit="selectedUnit"
      :units="units"
      @toggle-filter="toggleFilter"
      @unit-change="handleUnitChange"
    />

    <patient-table-header
      :sort-column="sortColumn"
      :sort-direction="sortDirection"
      :search-query="searchQuery"
      @sort="setSortColumn"
      @search="handleSearch"
    />

    <div class="home-view__content">
      <div v-if="loading" class="home-view__loading">
        <v-progress-circular indeterminate color="#6B46C1" />
        <span>Loading patients...</span>
      </div>

      <div v-else-if="error" class="home-view__error">
        <v-icon size="48" color="#D73D17">mdi-alert-circle</v-icon>
        <span>Failed to load patients. Please try again.</span>
        <v-btn color="#6B46C1" @click="loadPatients">Retry</v-btn>
      </div>

      <div v-else-if="patientGroups.length === 0" class="home-view__empty">
        <v-icon size="48" color="rgba(0, 0, 0, 0.38)">mdi-account-search</v-icon>
        <span>No patients found matching your filters.</span>
      </div>

      <div v-else class="home-view__groups">
        <patient-group-section
          v-for="group in patientGroups"
          :key="group.id"
          :group="group"
          @toggle="toggleGroupExpanded"
          @call="handleCall"
          @breakthrough="handleBreakthrough"
          @expand-row="handleRowExpand"
        />
      </div>
    </div>

    <call-dialog
      v-model="callDialogOpen"
      :patient-info="selectedPatient"
      :show-breakthrough="shouldShowBreakthrough"
      @confirm="confirmCall"
    />

    <settings-panel v-model="settingsPanelOpen" @close="closeSettings" />

    <!-- Availability Notification -->
    <v-snackbar
      v-model="showAvailabilityNotification"
      color="success"
      timeout="6000"
      location="top right"
      :close-on-content-click="false"
    >
      <div class="d-flex align-center">
        <v-icon start color="white">mdi-check-circle</v-icon>
        <div>
          <div class="font-weight-bold">{{ availabilityNotificationTitle }}</div>
          <div class="text-caption">{{ availabilityNotificationMessage }}</div>
        </div>
      </div>
      <template #actions>
        <v-btn
          variant="text"
          size="small"
          @click="showAvailabilityNotification = false"
        >
          Dismiss
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import PatientListHeader from "../components/PatientListHeader.vue";
import PatientFilterBar from "../components/PatientFilterBar.vue";
import PatientTableHeader from "../components/PatientTableHeader.vue";
import PatientGroupSection from "../components/PatientGroupSection.vue";
import CallDialog from "../components/CallDialog.vue";
import SettingsPanel from "../components/SettingsPanel.vue";
import { usePatientList } from "../composables/usePatientList";
import { usePatientActions } from "../composables/usePatientActions";
import { usePatientStore } from "../store/patientStore";
import { clearAuthToken } from "../utils/auth";
import { signalingService } from "../services/signalingService";
import type { PatientAvailabilityEvent } from "@virtual-care/shared";

const {
  patientGroups,
  totalPatientCount,
  filterCounts,
  selectedFilters,
  searchQuery,
  sortColumn,
  sortDirection,
  loading,
  error,
  toggleFilter,
  setSearchQuery,
  setSortColumn,
  toggleGroupExpanded,
  loadPatients,
} = usePatientList();

const {
  callDialogOpen,
  selectedPatientId,
  shouldShowBreakthrough,
  initiateCall,
  handleBreakthrough: initiateBreakthrough,
  confirmCall,
} = usePatientActions();

const patientStore = usePatientStore();
const router = useRouter();

const hospitalName = ref("Community Hospital");
const userName = ref(localStorage.getItem('vc_display_name') || 'Virtual Nurse');
const settingsPanelOpen = ref(false);

// Availability notification state
const showAvailabilityNotification = ref(false);
const availabilityNotificationTitle = ref("");
const availabilityNotificationMessage = ref("");

const units = computed(() => patientStore.units);
const selectedUnit = computed(() => patientStore.selectedUnit);

const selectedPatient = computed(() => {
  if (!selectedPatientId.value) return null;
  for (const group of patientGroups.value) {
    const patient = group.patients.find((p) => p.patientId === selectedPatientId.value);
    if (patient) return patient;
  }
  return null;
});

const handleUnitChange = (unitId: string) => {
  patientStore.setSelectedUnit(unitId);
};

const handleSettings = () => {
  settingsPanelOpen.value = true;
};

const closeSettings = () => {
  settingsPanelOpen.value = false;
};

const handleLogout = () => {
  clearAuthToken();
  router.push('/login');
};

const handleCall = (patientId: string) => {
  const patient = patientGroups.value.flatMap((g) => g.patients).find((p) => p.patientId === patientId);
  if (patient) initiateCall(patient);
};

const handleBreakthrough = (patientId: string) => {
  const patient = patientGroups.value.flatMap((g) => g.patients).find((p) => p.patientId === patientId);
  if (patient) initiateBreakthrough(patient);
};

const handleSearch = (query: string) => {
  setSearchQuery(query);
};

const handleRowExpand = (patientId: string) => {
  console.log("Row expanded:", patientId);
};

// Handle patient availability response
const handlePatientAvailability = (event: PatientAvailabilityEvent) => {
  console.log('[HomeView] Patient availability response:', event);
  
  // Find the patient by roomId
  const patient = patientGroups.value
    .flatMap((g) => g.patients)
    .find((p) => p.room.roomId === event.roomId);
  
  if (!patient) {
    console.warn('[HomeView] Patient not found for roomId:', event.roomId);
    return;
  }
  
  // Update patient store with availability status
  patientStore.setPatientAvailability(patient.patientId, event.response);
  
  // Show notification
  if (event.response === 'available') {
    availabilityNotificationTitle.value = `Patient Available - Room ${patient.room.roomNumber}`;
    availabilityNotificationMessage.value = `${patient.patientName} is now available for a call`;
  } else {
    availabilityNotificationTitle.value = `Patient Response - Room ${patient.room.roomNumber}`;
    availabilityNotificationMessage.value = `${patient.patientName} will call back later`;
  }
  
  showAvailabilityNotification.value = true;
};

onMounted(async () => {
  await patientStore.loadUnits();
  if (patientStore.selectedUnit) {
    loadPatients(patientStore.selectedUnit);
  }
  
  // Listen for patient availability responses
  signalingService.on('patient_availability', handlePatientAvailability);
  console.log('[HomeView] Listening for patient_availability events');
});

onBeforeUnmount(() => {
  // Clean up listener
  signalingService.off('patient_availability', handlePatientAvailability);
  console.log('[HomeView] Removed patient_availability listener');
});
</script>

<style lang="scss" scoped>
.home-view {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #fafafa;
  overflow: hidden;
}

.home-view__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, rgba(246, 245, 251, 1) 0%, rgba(227, 221, 243, 1) 100%);
  overflow-y: auto;
}

.home-view__loading,
.home-view__error,
.home-view__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px;
  font-family: "Proxima Nova", sans-serif;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.6);
}

.home-view__groups {
  display: flex;
  flex-direction: column;
}

@media (max-width: 767px) {
  .home-view__content {
    padding: 0;
  }
}
</style>
